<template>
    <template v-if="!isAGroup">
        <WalletTableMobileCard
            :key="group.id"
            :index="index"
            :totalCards="totalCards"
            :card="group.cards[0]"
            @edit="handleEdit"
            @delete="handleDelete"
            @view-details="handleViewDetails"
        />
    </template>

    <!-- Grouped Cards View -->
    <article
        v-else
        class="w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-100"
    >
        <div class="flex items-start gap-3 p-4">
            <!-- Card Stack Visualization -->
            <div class="relative w-20 h-12">
                <div class="card-stack relative w-full h-full">
                    <!-- Stacked card effect -->
                    <div
                        class="absolute w-[80%] h-full rounded-lg bg-slate-200 -bottom-1.5 left-1/2 z-0 -translate-x-1/2"
                    ></div>
                    <div
                        class="absolute w-[90%] h-full rounded-lg bg-slate-300 -bottom-1 left-1/2 z-10 -translate-x-1/2"
                    ></div>

                    <!-- Main card image -->
                    <ImageLoader class="relative z-20 w-full h-full">
                        <template #image>
                            <img
                                class="w-full h-full rounded border border-slate-200 dark:border-slate-700 object-cover relative z-20"
                                :src="`${cdnUrl}/${group.cards[0]?.card?.card_image?.path}/_small/${group.cards[0]?.card?.card_image?.file_name}`"
                                :alt="`${group.cards[0]?.name || 'Card'} image`"
                            />
                        </template>
                        <template #error>
                            <div
                                class="flex items-center justify-center w-full h-full rounded bg-slate-200"
                            >
                                <Icon icon="credit-card" class="h-6 w-6" />
                            </div>
                        </template>
                        <template #preloader>
                            <div
                                class="w-full h-full rounded border border-slate-200 dark:border-slate-700 object-cover bg-slate-200 shimmer"
                            ></div>
                        </template>
                    </ImageLoader>

                    <!-- Card count badge -->
                    <div
                        v-if="isAGroup"
                        class="absolute -right-2 -top-2 bg-slate-800 text-white text-xs font-medium rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center z-20"
                    >
                        {{ group.cards.length }}
                    </div>
                </div>
            </div>

            <!-- Card Info -->
            <div class="flex-1 min-h-10">
                <h3
                    v-if="group.cards[0]?.card_id"
                    class="text-slate-800 dark:text-slate-100 text-base font-medium leading-tight"
                >
                    {{ group.cards[0]?.card_title }}
                </h3>
                <h3 class="font-medium text-slate-800 dark:text-slate-100 truncate min-w-72" v-else>
                    {{ isAGroup ? 'Unknown cards' : 'Unknown card' }}
                </h3>
            </div>

            <!-- Expand/Collapse Button -->
            <button
                class="btn-base btn-icon"
                @click="toggleExpand"
                aria-label="Toggle card group"
            >
                <Icon
                    :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
                    class="w-5 h-5"
                />
            </button>
        </div>

        <!-- Expanded Cards -->
        <div
            v-if="isExpanded"
            class="mt-4 space-y-4 border-t border-slate-100 pt-3"
        >
            <WalletTableMobileCard
                v-for="(card, idx) in group.cards"
                :key="`card-${idx}`"
                :card="card"
                :index="idx"
                :total-cards="group.cards.length"
                :hasShadow="false"
                @edit="handleEdit"
                @delete="handleDelete"
                @view-details="handleViewDetails"
            />
        </div>
    </article>
</template>
<script setup>
import { useWalletStore } from '@/store/wallet';
import { storeToRefs } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';
import WalletTableMobileCard from '@/components/wallet/wallet-table-mobile-card.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    group: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        default: 0,
    },
    totalCards: {
        type: Number,
        required: true,
    },
});
const emit = defineEmits(['edit', 'delete', 'view-details']);

const walletStore = useWalletStore();
const { groupSameCards } = storeToRefs(walletStore);

const isExpanded = ref(false);
const expandedContent = ref(null);
const expandedHeight = ref('auto');
const cdnUrl = ref(window.cdnUrl);

const toggleExpand = () => {
    isExpanded.value = !isExpanded.value;
};
const numberOfCards = computed(() => {
    return props.group?.cards?.length || 0;
});

const isAGroup = computed(() => {
    return groupSameCards.value && numberOfCards.value > 1;
});

const handleEdit = (card) => {
    emit('edit', card);
};
const handleDelete = (card) => {
    emit('delete', card);
};
const handleViewDetails = (card) => {
    emit('view-details', card);
};

watch([isExpanded, () => props.group?.cards?.length], async (newVal) => {
    if (isExpanded.value) {
        expandedHeight.value = 'auto';
        await nextTick();
        if (expandedContent.value) {
            const height = expandedContent.value.scrollHeight;

            expandedHeight.value = height + 20 + 'px';
        }
    }
});
</script>
